import type { LearningExercise } from "../types";

// Markenrecht — Terminе 14+15 (Überblick + materielles Recht)
// Abdeckung: § 3 (Zeichenarten, Formausschlüsse), § 4 (Entstehung), § 8 (absolute Schutzhindernisse,
// Verkehrsdurchsetzung), § 9 (Verwechslungsgefahr, Wechselwirkungslehre), § 14 (Verletzungstatbestände),
// § 23 (Schranken), § 24 (Erschöpfung), § 26 (Benutzungspflicht), § 49 (Verfall), § 50/51 (Nichtigkeit),
// § 42 (Widerspruch); Unionsmarkenbezug (Art. 7/8 UMV).
// Quellen inline (sources-Array), pathId "deutsche-ausbildung", mode "ausbildung", reviewStatus "draft".
// IDs: da-marke-001 … da-marke-040.
// Fallkonstellation-Items: da-marke-010, -011, -016, -017, -018, -019, -022, -023, -027, -030, -034, -035, -036 (≥ 32 %)
// reviewStatus "draft" für alle Items — Freigabe ausschließlich durch question-reviewer.
export const ausbildungMarke: LearningExercise[] = [

  // ─── FLASHCARDS da-marke-001 … da-marke-008 ──────────────────────────────

  {
    id: "da-marke-001",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 3 — Schutzfähige Zeichen",
    title: "Markenfähigkeit: abstrakte Schutzvoraussetzungen",
    prompt:
      "Welche zwei abstrakten Voraussetzungen muss ein Zeichen nach § 3 Abs. 1 MarkenG erfüllen, um als Marke eingetragen werden zu können?",
    options: [],
    answers: [],
    explanation:
      "§ 3 Abs. 1 MarkenG: Als Marke können alle Zeichen eingetragen werden, die (1) geeignet sind, Waren oder Dienstleistungen eines Unternehmens von denjenigen anderer Unternehmen zu unterscheiden (Unterscheidungseignung), und (2) grafisch darstellbar sind (nach der Reform 2019: im Register in einer Weise dargestellt werden können, die es ermöglicht, den Gegenstand des Schutzes klar und eindeutig zu bestimmen). Wortmarken, Bildmarken, Formmarken, Farbmarken, Hörmarken u.a. fallen darunter — sofern die abstrakten Voraussetzungen erfüllt sind. Die konkreten Schutzhindernisse (§ 8 MarkenG) werden davon getrennt geprüft.",
    sourceIds: ["markenG3"],
    sources: [
      { id: "markenG3", label: "§ 3 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__3.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["markenrecht", "markenfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-002",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 3 Abs. 2 — Formausschlüsse",
    title: "Technisch/wertbedingte Formausschlüsse (§ 3 Abs. 2 MarkenG)",
    prompt:
      "Welche drei Fallgruppen von Formen schließt § 3 Abs. 2 MarkenG absolut von der Markenfähigkeit aus, und warum sind diese Ausschlüsse nicht durch Verkehrsdurchsetzung überwindbar?",
    options: [],
    answers: [],
    explanation:
      "§ 3 Abs. 2 MarkenG schließt drei Formengruppen aus: Nr. 1 — Formen, die durch die Art der Ware selbst bedingt sind (Notwendigkeitsformen); Nr. 2 — Formen, die zur Erreichung einer technischen Wirkung erforderlich sind (technisch bedingte Formen; EuGH 'Philips/Remington', C-299/99); Nr. 3 — Formen, die der Ware einen wesentlichen Wert verleihen (ästhetisch/wertbedingte Formen). Diese Ausschlüsse sind absolut, weil die dahinter stehenden Interessen (Wettbewerbsfreiheit, freier Technologiezugang) eine Monopolisierung dauerhaft verbieten — eine Verkehrsdurchsetzung nach § 8 Abs. 3 MarkenG ist deshalb nicht möglich.",
    sourceIds: ["markenG3"],
    sources: [
      { id: "markenG3", label: "§ 3 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "markenfaehigkeit", "formmarke"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-003",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 4 — Entstehung des Markenschutzes",
    title: "Drei Entstehungswege des Markenschutzes (§ 4 MarkenG)",
    prompt:
      "Auf welchen drei Wegen entsteht Markenschutz nach § 4 MarkenG?",
    options: [],
    answers: [],
    explanation:
      "§ 4 MarkenG nennt drei Entstehungswege: (1) § 4 Nr. 1 — Eintragung in das beim DPMA geführte Register (Regelfall für eingetragene Marken; Zeitrang = Anmeldetag, § 6 Abs. 2 MarkenG); (2) § 4 Nr. 2 — Benutzung im geschäftlichen Verkehr, soweit das Zeichen innerhalb der beteiligten Verkehrskreise als Marke gilt (Benutzungsmarke / Verkehrsgeltungsmarke; Nachweis erforderlich, typisch ca. 50 % Bekanntheit); (3) § 4 Nr. 3 — notorische Bekanntheit i.S.d. Art. 6bis PVÜ im Inland (international bekannte Marken, auch ohne Benutzung oder Eintragung in Deutschland).",
    sourceIds: ["markenG4"],
    sources: [
      { id: "markenG4", label: "§ 4 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__4.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["markenrecht", "schutzentstehung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-004",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 8 Abs. 3 — Verkehrsdurchsetzung",
    title: "Verkehrsdurchsetzung: Voraussetzungen und Wirkung",
    prompt:
      "Unter welchen Voraussetzungen kann ein an sich schutzunfähiges Zeichen gemäß § 8 Abs. 3 MarkenG dennoch eingetragen werden, und welche Hindernisse überwindet die Verkehrsdurchsetzung?",
    options: [],
    answers: [],
    explanation:
      "§ 8 Abs. 3 MarkenG: Hindernisse aus § 8 Abs. 2 Nr. 1 (fehlende Unterscheidungskraft), Nr. 2 (beschreibende Angaben) und Nr. 3 (Gattungsbezeichnungen) können überwunden werden, wenn das Zeichen infolge seiner Benutzung Unterscheidungskraft erworben hat. Voraussetzungen: (1) intensive, langandauernde Benutzung des Zeichens im Inland vor Anmeldung; (2) dadurch erlangter Erwerb von Unterscheidungskraft bei den maßgeblichen Verkehrskreisen; (3) Bekanntheit bei diesen Kreisen (typisch 50–75 %+ je nach Zeichen). Nicht überwindbar durch § 8 Abs. 3 sind die Ausschlüsse nach § 3 Abs. 2 MarkenG (technisch/wertbedingte Formen) sowie § 8 Abs. 2 Nr. 4–10 (Täuschungsgefahr, Hoheitszeichen etc.).",
    sourceIds: ["markenG8"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["markenrecht", "schutzhindernisse", "verkehrsdurchsetzung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-005",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 26 — Benutzungspflicht",
    title: "Benutzungszwang und Benutzungsschonfrist (§ 26 MarkenG)",
    prompt:
      "Innerhalb welcher Frist muss eine eingetragene Marke nach § 26 MarkenG ernsthaft benutzt werden, und welche Rechtsfolgen drohen bei Nichtbenutzung?",
    options: [],
    answers: [],
    explanation:
      "§ 26 Abs. 1 MarkenG: Die Marke muss innerhalb von fünf Jahren nach der Eintragung für die Waren oder Dienstleistungen, für die sie eingetragen ist, ernsthaft im Inland benutzt worden sein; andernfalls drohen Rechtsnachteile. Rechtsfolgen der Nichtbenutzung: (1) Verfall der Marke nach § 49 Abs. 1 MarkenG (ex nunc); (2) Einrede mangelnder Benutzung im Verletzungsverfahren (§ 25 MarkenG); (3) Nichtberücksichtigung im Widerspruchsverfahren (§ 43 MarkenG). Benutzung in abgewandelter Form schadet nach § 26 Abs. 3 MarkenG nicht, wenn die Unterscheidungskraft der Marke nicht verändert wird.",
    sourceIds: ["markenG26"],
    sources: [
      { id: "markenG26", label: "§ 26 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__26.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["markenrecht", "benutzungspflicht", "verfall"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-006",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG §§ 49–51 — Verfall und Nichtigkeit",
    title: "Verfall (§ 49) vs. Nichtigkeit (§§ 50, 51 MarkenG): Zeitliche Wirkung",
    prompt:
      "Was unterscheidet Verfall (§ 49 MarkenG) von Nichtigkeit (§§ 50, 51 MarkenG) hinsichtlich der zeitlichen Wirkung und der Gründe?",
    options: [],
    answers: [],
    explanation:
      "Verfall (§ 49 MarkenG): Tritt nachträglich ein (ex nunc — Marke verliert Schutz ab dem Zeitpunkt des Verfallsgrundes, nicht rückwirkend). Gründe: § 49 Abs. 1 — fünf Jahre Nichtbenutzung (§ 26); § 49 Abs. 2 Nr. 1 — Verwandlung in eine im Verkehr üblich gewordene Gattungsbezeichnung; § 49 Abs. 2 Nr. 2 — irreführend geworden. Nichtigkeit (§ 50/51 MarkenG): Eintragung hätte nie erfolgen dürfen — ex tunc (Marke gilt als von Anfang an nicht eingetragen). § 50 — absolute Nichtigkeitsgründe (z.B. § 8 Abs. 2, bösgläubige Anmeldung); § 51 — relative Nichtigkeitsgründe (ältere kollidierende Rechte). Entsprechend gilt Art. 58/59/60 UMV für die Unionsmarke.",
    sourceIds: ["markenG49", "markenG50", "markenG51"],
    sources: [
      { id: "markenG49", label: "§ 49 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__49.html" },
      { id: "markenG50", label: "§ 50 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__50.html" },
      { id: "markenG51", label: "§ 51 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__51.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "verfall", "nichtigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-007",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 24 — Erschöpfung",
    title: "Markenrechtliche Erschöpfung (§ 24 MarkenG)",
    prompt:
      "Unter welchen Voraussetzungen tritt Erschöpfung des Markenrechts nach § 24 MarkenG ein, und was sind die wichtigsten Grenzen der Erschöpfung?",
    options: [],
    answers: [],
    explanation:
      "§ 24 Abs. 1 MarkenG: Erschöpfung tritt ein, wenn der Markeninhaber oder ein mit seiner Zustimmung handelnder Dritter Waren unter der Marke im Inland (EU/EWR) in den Verkehr gebracht hat. Danach kann der Markeninhaber die weitere Benutzung der Marke für diese Waren nicht untersagen. Wichtige Grenzen (§ 24 Abs. 2 MarkenG): Der Markeninhaber kann sich der weiteren Benutzung widersetzen, wenn berechtigte Gründe vorliegen — insbesondere wenn der Zustand der Waren nach dem Inverkehrbringen verändert oder verschlechtert wurde (Umpackfälle). Territorialer Geltungsbereich: Erschöpfung gilt nur für den EWR — Import aus Drittstaaten (z.B. USA, Japan) ohne Zustimmung des Markeninhabers verletzt das Markenrecht (keine internationale Erschöpfung).",
    sourceIds: ["markenG24"],
    sources: [
      { id: "markenG24", label: "§ 24 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__24.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["markenrecht", "erschoepfung", "schranken"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-008",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "MarkenG § 9 — Verwechslungsgefahr",
    title: "Wechselwirkungsprinzip (EuGH Canon) — Kernaussage",
    prompt:
      "Was besagt das Wechselwirkungsprinzip bei der Beurteilung der Verwechslungsgefahr nach § 9 MarkenG (EuGH 'Canon', C-39/97)?",
    options: [],
    answers: [],
    explanation:
      "Das Wechselwirkungsprinzip (EuGH 'Canon', C-39/97, zu Art. 8 Abs. 1 lit. b GMV): Zwischen Zeichenähnlichkeit, Waren-/Dienstleistungsähnlichkeit und Kennzeichnungskraft der älteren Marke besteht eine Wechselwirkung. Ein geringerer Grad an Waren-/DL-Ähnlichkeit kann durch einen höheren Grad an Zeichenähnlichkeit (oder stärkere Kennzeichnungskraft) ausgeglichen werden und umgekehrt. Die Verwechslungsgefahr ist daher das Ergebnis einer umfassenden Gesamtabwägung aller relevanten Faktoren. Im Rahmen von § 9 Abs. 1 Nr. 2 MarkenG gilt: selbst bei nur mäßiger Zeichenähnlichkeit kann Verwechslungsgefahr vorliegen, wenn die ältere Marke eine hohe originäre oder durch Benutzung erworbene Kennzeichnungskraft besitzt.",
    sourceIds: ["markenG9"],
    sources: [
      { id: "markenG9", label: "§ 9 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__9.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["markenrecht", "verwechslungsgefahr", "wechselwirkung"],
    reviewStatus: "beta-ready",
  },

  // ─── MC SINGLE / MULTI da-marke-009 … da-marke-031 ───────────────────────

  {
    id: "da-marke-009",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 8 Abs. 2 Nr. 1 — Unterscheidungskraft",
    title: "Fehlende Unterscheidungskraft — Beispiele",
    prompt:
      "Welches der folgenden Zeichen würde das DPMA am ehesten wegen fehlender Unterscheidungskraft (§ 8 Abs. 2 Nr. 1 MarkenG) zurückweisen?",
    options: [
      "Das Fantasiewort 'ZOMEKO' für Haushaltsgeräte",
      "Eine kreisförmige Farbkombination aus Orange und Blau für Sporttrikots",
      "Das Wort 'SMARTPHONE' für mobile Endgeräte",
      "Eine stilisierte Drache-Figur als Bildmarke für Spielzeug",
    ],
    answers: [2],
    explanation:
      "§ 8 Abs. 2 Nr. 1 MarkenG: Zeichen ohne jede Unterscheidungskraft sind von der Eintragung ausgeschlossen. 'SMARTPHONE' ist für mobile Endgeräte eine glatt beschreibende Gattungsbezeichnung und damit ein klassischer Fall fehlender Unterscheidungskraft — das Zeichen kann nicht als Hinweis auf die betriebliche Herkunft verstanden werden. Fantasieworte wie 'ZOMEKO', kreative Farbkombinationen und stilisierte Figuren können dagegen grundsätzlich Unterscheidungskraft haben.",
    sourceIds: ["markenG8"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "schutzhindernisse", "unterscheidungskraft"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-010",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 3 Abs. 2 — Formausschlüsse",
    title: "Fall: technisch bedingte Form (§ 3 Abs. 2 Nr. 2 MarkenG)",
    prompt:
      "Hersteller H produziert einen Rasierapparat mit drei kreisförmig angeordneten Klingenköpfen. Diese Anordnung ist technisch notwendig für die Rasierleistung. H möchte die Form als dreidimensionale Marke eintragen lassen. Kann er durch den Nachweis von Verkehrsdurchsetzung die Eintragungsschranke überwinden?",
    options: [
      "Ja — Verkehrsdurchsetzung (§ 8 Abs. 3 MarkenG) überwindet alle Eintragungshindernisse ohne Ausnahme.",
      "Nein — technisch bedingte Formen sind nach § 3 Abs. 2 Nr. 2 MarkenG absolut von der Markenfähigkeit ausgeschlossen; Verkehrsdurchsetzung hilft nicht.",
      "Nein — aber H könnte die Form als Geschmacksmuster (Designrecht) schützen lassen.",
      "Ja — der Ausschluss gilt nur für zweidimensionale Marken, nicht für dreidimensionale Formen.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 2 Nr. 2 MarkenG (entsprechend EuGH 'Philips/Remington', C-299/99): Formen, die zur Erreichung einer technischen Wirkung erforderlich sind, sind absolut von der Markenfähigkeit ausgeschlossen. Dieser Ausschluss wirkt auf der Ebene der abstrakten Markenfähigkeit (§ 3 MarkenG) — er ist deshalb nicht durch Verkehrsdurchsetzung nach § 8 Abs. 3 MarkenG heilbar. Designschutz wäre ein anderer Schutzweg, aber der Ausschluss des Markenrechts bleibt davon unberührt.",
    sourceIds: ["markenG3"],
    sources: [
      { id: "markenG3", label: "§ 3 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "formmarke", "markenfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-011",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 4 Nr. 2 — Benutzungsmarke",
    title: "Fall: Benutzungsmarke — Voraussetzungen",
    prompt:
      "Unternehmerin U vertreibt seit drei Jahren regional begrenzt Schokoladenprodukte unter der Bezeichnung 'CHOCOTIME'. Eine Eintragung beim DPMA hat sie nicht vorgenommen. Ihr Mitbewerber M möchte die Bezeichnung bundesweit verwenden. Kann U Schutzrechte geltend machen?",
    options: [
      "Nein — ohne Eintragung beim DPMA besteht kein Markenschutz.",
      "Ja — wenn 'CHOCOTIME' bei den relevanten Verkehrskreisen im Benutzungsgebiet als Marke gilt (§ 4 Nr. 2 MarkenG), entsteht Schutz durch Benutzung.",
      "Ja — weil U die Marke drei Jahre benutzt hat, entsteht Schutz automatisch kraft Zeitablaufs.",
      "Nein — Benutzungsmarken sind im deutschen Recht seit 2019 abgeschafft.",
    ],
    answers: [1],
    explanation:
      "§ 4 Nr. 2 MarkenG: Markenschutz entsteht auch durch Benutzung im geschäftlichen Verkehr, sofern das Zeichen innerhalb der beteiligten Verkehrskreise als Marke gilt (Verkehrsgeltung). Eine Eintragung ist dafür nicht erforderlich. Allerdings ist der Schutz auf das Gebiet beschränkt, in dem die Verkehrsgeltung besteht (terr. Begrenzung). Ein bloßer Zeitablauf (drei Jahre) genügt nicht — maßgeblich ist der Bekanntheitsgrad bei den relevanten Verkehrskreisen (typisch ~50 %).",
    sourceIds: ["markenG4"],
    sources: [
      { id: "markenG4", label: "§ 4 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__4.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "schutzentstehung", "benutzungsmarke"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-012",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 8 Abs. 2 Nr. 2 — Freihaltebedürfnis",
    title: "Freihaltebedürfnis vs. Verkehrsdurchsetzung",
    prompt:
      "Das Zeichen 'BIO-FRESH' soll für Lebensmittel eingetragen werden. Das DPMA lehnt wegen beschreibender Angabe ab (§ 8 Abs. 2 Nr. 2 MarkenG). Der Anmelder weist nach, dass sich das Zeichen infolge intensiver Benutzung im Verkehr durchgesetzt hat. Wie ist die Rechtslage?",
    options: [
      "Der Anmelder hat Recht — Verkehrsdurchsetzung (§ 8 Abs. 3) überwindet auch § 8 Abs. 2 Nr. 2.",
      "Das DPMA hat Recht — § 8 Abs. 2 Nr. 2 (Freihaltebedürfnis) ist nach h.M. auch durch Verkehrsdurchsetzung nicht überwindbar.",
      "Das Ergebnis hängt davon ab, ob die Beschreibungsnähe 'mittelbar' oder 'unmittelbar' ist.",
      "Das DPMA muss die Eintragung zulassen, wenn die Benutzung länger als fünf Jahre nachgewiesen wird.",
    ],
    answers: [0],
    explanation:
      "§ 8 Abs. 3 MarkenG: Die absoluten Schutzhindernisse des § 8 Abs. 2 Nr. 1, 2 und 3 (fehlende Unterscheidungskraft, beschreibende Angaben/Freihaltebedürfnis, Gattungsbezeichnungen) finden keine Anwendung, wenn sich die Marke vor der Entscheidung über die Eintragung infolge ihrer Benutzung in den beteiligten Verkehrskreisen durchgesetzt hat (Verkehrsdurchsetzung). Da § 8 Abs. 3 ausdrücklich auch Nr. 2 erfasst, ist das Freihaltebedürfnis durch Verkehrsdurchsetzung überwindbar — Option A ist richtig. Erforderlich ist allerdings ein hoher, durch Nachweise belegter Durchsetzungsgrad. Distraktor D ist falsch: § 8 Abs. 3 verlangt keine starre Fünf-Jahres-Benutzung, sondern den Nachweis der Verkehrsdurchsetzung.",
    sourceIds: ["markenG8"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
    ],
    level: "Schwer",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "schutzhindernisse", "freihaltebeduernis", "verkehrsdurchsetzung"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-013",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 42 — Widerspruch",
    title: "Widerspruchsfrist und -verfahren (§ 42 MarkenG)",
    prompt:
      "Innerhalb welcher Frist kann nach § 42 MarkenG Widerspruch gegen eine eingetragene Marke beim DPMA eingelegt werden?",
    options: [
      "6 Monate ab Veröffentlichung der Eintragung",
      "3 Monate ab Veröffentlichung der Eintragung",
      "1 Jahr ab Eintragungsdatum",
      "2 Monate ab Bekanntgabe der Eintragung an den Anmelder",
    ],
    answers: [1],
    explanation:
      "§ 42 Abs. 1 MarkenG: Innerhalb einer Frist von drei Monaten nach dem Tag der Veröffentlichung der Eintragung kann gegen eine eingetragene Marke Widerspruch eingelegt werden. Widerspruchsberechtigt sind Inhaber älterer Marken oder geschäftlicher Bezeichnungen. Im Widerspruchsverfahren kann der Inhaber der jüngeren Marke die Benutzung der älteren Marke einfordern (§ 43 MarkenG — Glaubhaftmachung der Benutzung), wenn die Schonfrist von fünf Jahren abgelaufen ist.",
    sourceIds: ["markenG42"],
    sources: [
      { id: "markenG42", label: "§ 42 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__42.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["markenrecht", "verfahren", "widerspruch"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-014",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 47 — Schutzdauer",
    title: "Schutzdauer der eingetragenen Marke",
    prompt:
      "Wie lange dauert der Schutz einer eingetragenen deutschen Marke nach § 47 MarkenG, und was ist für die Aufrechterhaltung erforderlich?",
    options: [
      "20 Jahre ab Anmeldetag, nicht verlängerbar",
      "10 Jahre ab Anmeldetag, unbegrenzt verlängerbar um je 10 Jahre gegen Gebühr",
      "25 Jahre ab Anmeldetag, verlängerbar um je 25 Jahre",
      "10 Jahre ab Eintragungsdatum, einmalig verlängerbar",
    ],
    answers: [1],
    explanation:
      "§ 47 Abs. 1 MarkenG: Die Schutzdauer einer eingetragenen Marke beträgt zehn Jahre ab dem Anmeldetag. § 47 Abs. 2 MarkenG: Die Schutzdauer kann auf Antrag des Markeninhabers gegen Entrichtung der vorgeschriebenen Verlängerungsgebühr jeweils um zehn Jahre verlängert werden. Es gibt keine zeitliche Obergrenze — die Verlängerung ist unbegrenzt möglich, sodass Markenschutz theoretisch unendlich bestehen kann (im Unterschied zum Patentschutz mit absoluter Laufzeitbegrenzung).",
    sourceIds: ["markenG47"],
    sources: [
      { id: "markenG47", label: "§ 47 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__47.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["markenrecht", "schutzdauer"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-015",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 14 Abs. 2 — Verletzungstatbestände",
    title: "Verletzungstatbestände: Doppelidentität, Verwechslungsgefahr, Bekanntheitsschutz",
    prompt:
      "Welcher Verletzungstatbestand des § 14 Abs. 2 MarkenG setzt gerade keine Verwechslungsgefahr voraus?",
    options: [
      "§ 14 Abs. 2 Nr. 2 MarkenG — Verwechslungsgefahr bei Zeichen-/Warenähnlichkeit",
      "§ 14 Abs. 2 Nr. 1 MarkenG — Doppelidentität (identisches Zeichen, identische Waren/DL)",
      "§ 14 Abs. 2 Nr. 3 MarkenG — Bekanntheitsschutz (ohne Warenähnlichkeit erforderlich)",
      "Alle Tatbestände des § 14 Abs. 2 MarkenG setzen Verwechslungsgefahr voraus.",
    ],
    answers: [1],
    explanation:
      "§ 14 Abs. 2 Nr. 1 MarkenG (Doppelidentität): Bei vollständiger Identität von Zeichen UND Waren/Dienstleistungen liegt die Verletzung bereits vor, ohne dass Verwechslungsgefahr geprüft werden muss — sie wird unwiderleglich vermutet. § 14 Abs. 2 Nr. 2 (Verwechslungsgefahr) und Nr. 3 (Bekanntheitsschutz) setzen keine vollständige Identität voraus, erfordern aber eine positive Feststellung von Verwechslungsgefahr (Nr. 2) bzw. Ausnutzung/Beeinträchtigung (Nr. 3).",
    sourceIds: ["markenG14"],
    sources: [
      { id: "markenG14", label: "§ 14 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__14.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "verletzung", "doppelidentitaet"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-016",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 14 Abs. 2 Nr. 3 — Bekanntheitsschutz",
    title: "Fall: Bekanntheitsschutz — Verwässerung",
    prompt:
      "Das bekannte Energydrink-Unternehmen 'RedBull GmbH' (eingetragene Marke für Getränke) klagt gegen einen Anbieter von Angelzubehör, der unter 'RedBull' handelt. Warenähnlichkeit besteht offensichtlich nicht. Auf welchen Tatbestand stützt sich die Klage am ehesten?",
    options: [
      "§ 14 Abs. 2 Nr. 2 MarkenG — Verwechslungsgefahr bei Warenähnlichkeit",
      "§ 14 Abs. 2 Nr. 1 MarkenG — Doppelidentität",
      "§ 14 Abs. 2 Nr. 3 MarkenG — Bekanntheitsschutz, der auch ohne Warenähnlichkeit gilt",
      "§ 23 MarkenG — Schranke zugunsten des Angelzubehör-Anbieters",
    ],
    answers: [2],
    explanation:
      "§ 14 Abs. 2 Nr. 3 MarkenG: Bekannte Marken genießen Schutz auch über den Bereich der Warenähnlichkeit hinaus, wenn die Benutzung des jüngeren Zeichens die Unterscheidungskraft oder Wertschätzung der bekannten Marke ohne rechtfertigenden Grund in unlauterer Weise ausnutzt oder beeinträchtigt (Verwässerung, Rufschädigung, Trittbrettfahren). Voraussetzungen: Bekanntheit der älteren Marke bei einem erheblichen Teil des Publikums, gedankliche Verknüpfung beim Publikum. Warenähnlichkeit ist ausdrücklich nicht erforderlich.",
    sourceIds: ["markenG14"],
    sources: [
      { id: "markenG14", label: "§ 14 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__14.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "verletzung", "bekanntheitsschutz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-017",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 23 — Schranken",
    title: "Fall: Bestimmungshinweis (§ 23 Abs. 1 Nr. 3 MarkenG)",
    prompt:
      "Automobilhändler H wirbt für seine Werkstatt mit dem Satz: 'Wir reparieren auch BMW und Mercedes'. Beide Hersteller besitzen eingetragene Marken für diese Bezeichnungen. Verletzt H die Marken?",
    options: [
      "Ja — jede Verwendung fremder Marken im geschäftlichen Verkehr ist Verletzung.",
      "Nein — § 23 Abs. 1 Nr. 3 MarkenG erlaubt die Verwendung als Hinweis auf die Bestimmung der Dienstleistung (Kompatibilitätshinweis), sofern dies den anständigen Gepflogenheiten entspricht.",
      "Nein — Erschöpfung nach § 24 MarkenG tritt ein, weil BMW und Mercedes Fahrzeuge in Verkehr gebracht haben.",
      "Ja — § 23 MarkenG gilt nur für Einzelpersonen, nicht für Gewerbetreibende.",
    ],
    answers: [1],
    explanation:
      "§ 23 Abs. 1 Nr. 3 MarkenG: Die Verwendung einer Marke als Hinweis auf die Bestimmung einer Ware oder Dienstleistung (insbesondere als Zubehör- oder Ersatzteilangabe) ist zulässig, sofern sie den anständigen Gepflogenheiten in Gewerbe oder Handel entspricht (Lauterkeitskorrektiv). Ein Kfz-Händler, der angibt, Fahrzeuge der Marken BMW und Mercedes zu reparieren, benutzt die Zeichen als Bestimmungshinweis — das ist eine erlaubte beschreibende Benutzung nach § 23 Abs. 1 Nr. 3 MarkenG, keine Verletzung.",
    sourceIds: ["markenG23"],
    sources: [
      { id: "markenG23", label: "§ 23 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__23.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "schranken", "beschreibende-benutzung"],
    // REVIEW (question-reviewer 2026-06-03): title-Zitat "§ 23 Abs. 1 Nr. 2" auf "Nr. 3"
    // korrigiert (FIX) — Bestimmungshinweis/Kompatibilitaet ist post-MaMoG § 23 Abs. 1 Nr. 3;
    // Antwortschluessel und Explanation waren bereits korrekt auf Nr. 3.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-018",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 24 — Erschöpfung",
    title: "Fall: Erschöpfung und Umpacken (§ 24 MarkenG)",
    prompt:
      "Importeur I kauft in den USA Originalware des Markeninhabers M zu günstigeren Preisen und importiert sie nach Deutschland. M hat die Waren nicht für den EWR freigegeben. Kann M den Import untersagen?",
    options: [
      "Nein — Erschöpfung tritt auch bei Inverkehrbringen außerhalb des EWR ein (internationale Erschöpfung).",
      "Nein — da es Originalware ist, liegt keine Markenverletzung vor.",
      "Ja — § 24 MarkenG begründet nur eine EWR-weite Erschöpfung; Import aus Drittstaaten ohne Zustimmung verletzt das Markenrecht.",
      "Ja — aber nur, wenn M nachweist, dass die Waren verändert wurden.",
    ],
    answers: [2],
    explanation:
      "§ 24 Abs. 1 MarkenG setzt voraus, dass der Markeninhaber oder ein mit seiner Zustimmung handelnder Dritter die Waren im Inland oder in einem der EU-Mitgliedstaaten oder einem anderen Vertragsstaat des EWR-Abkommens in den Verkehr gebracht hat. Das deutsche und europäische Markenrecht kennt keine internationale Erschöpfung — Inverkehrbringen außerhalb des EWR (z.B. USA) erschöpft das Markenrecht nicht. M kann den Import daher untersagen.",
    sourceIds: ["markenG24"],
    sources: [
      { id: "markenG24", label: "§ 24 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__24.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "erschoepfung", "parallelimport"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-019",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 49 — Verfall",
    title: "Fall: Verfall wegen Nichtbenutzung (§ 49 Abs. 1 MarkenG)",
    prompt:
      "Marke 'SOLARA' ist seit sieben Jahren eingetragen. Der Inhaber hat die Marke in den letzten sechs Jahren nicht benutzt. Konkurrent K möchte die Marke verwenden und stellt einen Verfallsantrag. Was ist die Rechtsfolge?",
    options: [
      "Die Marke erlischt ex tunc — sie gilt als nie eingetragen.",
      "Die Marke kann nicht verfallen, weil die Benutzungsschonfrist von fünf Jahren bereits längst abgelaufen ist.",
      "Die Marke verfällt ex nunc — sie verliert ihren Schutz ab dem Zeitpunkt der Feststellung des Verfalls.",
      "K muss zunächst einen Widerspruch nach § 42 MarkenG einlegen.",
    ],
    answers: [2],
    explanation:
      "§ 49 Abs. 1 MarkenG: Die Marke wird auf Antrag für verfallen erklärt und aus dem Register gelöscht, wenn die Marke nach dem für die Eintragung maßgeblichen Zeitpunkt für einen ununterbrochenen Zeitraum von fünf Jahren nicht ernsthaft benutzt worden ist. Rechtsfolge: Verfall wirkt ex nunc (nicht rückwirkend) — die Marke verliert ihren Schutz erst ab dem Verfallszeitpunkt. Dies unterscheidet den Verfall von der Nichtigkeit (§§ 50, 51 MarkenG), die ex tunc wirkt. Der Verfallsantrag ist nicht identisch mit dem Widerspruch nach § 42 MarkenG (der gilt nur für die Widerspruchsfrist von drei Monaten nach Eintragungsveröffentlichung).",
    sourceIds: ["markenG49"],
    sources: [
      { id: "markenG49", label: "§ 49 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__49.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["markenrecht", "verfall", "benutzungspflicht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-020",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 9 Abs. 1 — Verwechslungsgefahr",
    title: "Verwechslungsgefahr — Prüfungsreihenfolge",
    prompt:
      "In welcher Reihenfolge sind die Prüfungsstufen der Verwechslungsgefahr nach § 9 Abs. 1 Nr. 2 MarkenG im Gutachten abzuhandeln?",
    options: [
      "1. Markenähnlichkeit → 2. Warenähnlichkeit → 3. Kennzeichnungskraft → 4. Gesamtabwägung",
      "1. Kennzeichnungskraft → 2. Warenähnlichkeit → 3. Markenähnlichkeit → 4. Gesamtabwägung",
      "1. Warenähnlichkeit → 2. Markenähnlichkeit → 3. Kennzeichnungskraft der älteren Marke → 4. Gesamtabwägung (Wechselwirkung)",
      "1. Gesamtabwägung → 2. Warenähnlichkeit → 3. Markenähnlichkeit",
    ],
    answers: [2],
    explanation:
      "Das herrschende Prüfungsschema zur Verwechslungsgefahr (§ 9 Abs. 1 Nr. 2 MarkenG, EuGH 'Canon', C-39/97) gliedert sich in: (1) Warenähnlichkeit/Dienstleistungsähnlichkeit — wenn keine Ähnlichkeit, keine Verwechslungsgefahr ohne Weiteres; (2) Markenähnlichkeit — visuell, klanglich, begrifflich, Gesamteindruck; (3) Kennzeichnungskraft der älteren Marke — originäre oder durch Benutzung erworbene Stärke; (4) Gesamtabwägung nach dem Wechselwirkungsprinzip. Die Reihenfolge Warenähnlichkeit vor Markenähnlichkeit ist gutachtlich sauber zu halten.",
    sourceIds: ["markenG9"],
    sources: [
      { id: "markenG9", label: "§ 9 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__9.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["markenrecht", "verwechslungsgefahr", "pruefungsschema"],
    // REVIEW (question-reviewer 2026-06-03): als Einzel-MC eindeutig beantwortbar (Key idx 2:
    // Waren → Marken → Kennzeichnungskraft → Abwaegung; Distraktoren sind klar falsche Sequenzen).
    // HINWEIS an Author/Curator: Reihenfolge widerspricht da-marke-037 (dort Kennzeichnungskraft
    // ZUERST). Beide Sequenzen sind vertretbar, aber zwei verschiedene "richtige" Schemata im selben
    // Modul sind didaktisch inkonsistent — bitte modulweit auf EINE Reihenfolge vereinheitlichen.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-021",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "UMV Art. 7 / MarkenG § 8 — Absolute Hindernisse",
    title: "Unionsmarke vs. nationale Marke — absolute Schutzhindernisse",
    prompt:
      "Welche Aussage zum Verhältnis zwischen § 8 MarkenG und Art. 7 UMV (absolute Eintragungshindernisse) ist zutreffend?",
    options: [
      "§ 8 MarkenG und Art. 7 UMV sind vollständig identisch; nationale und Unionsmarke werden nach denselben Normen geprüft.",
      "Art. 7 UMV enthält weitgehend inhaltsgleiche Hindernisse wie § 8 MarkenG, da beide auf der Markenrechtsrichtlinie beruhen; die Prüfung erfolgt aber getrennt durch DPMA (national) und EUIPO (Unionsmarke).",
      "Art. 7 UMV ist weniger streng als § 8 MarkenG — Unionsmarken können daher leichter eingetragen werden.",
      "Bei einer Unionsmarke werden absolute Schutzhindernisse gar nicht von Amts wegen geprüft.",
    ],
    answers: [1],
    explanation:
      "Art. 7 UMV und § 8 MarkenG enthalten strukturell und inhaltlich weitgehend übereinstimmende absolute Eintragungshindernisse (beide basieren auf der harmonisierenden Markenrechtsrichtlinie). Sie werden jedoch getrennt angewendet: Das DPMA prüft bei nationalen Anmeldungen § 8 MarkenG von Amts wegen; das EUIPO prüft Art. 7 UMV bei Unionsmarkenanmeldungen. Die Prüfungsstrenge kann in der Praxis variieren. Für Unionsmarken gilt das Einheitsprinzip: Eine Schutzunfähigkeit in auch nur einem EU-Mitgliedstaat kann zur Zurückweisung führen (Art. 7 Abs. 2 UMV).",
    sourceIds: ["markenG8", "umv7"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
      { id: "umv7", label: "Art. 7 UMV", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "unionsmarke", "schutzhindernisse"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-022",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 9 — Verwechslungsgefahr Wechselwirkung",
    title: "Fall: Wechselwirkung — schwache Marke mit hoher Warenidentität",
    prompt:
      "Die ältere Marke 'QUALITÄT' (für Bürostühle, schwache Kennzeichnungskraft wegen Beschreibungsnähe) trifft auf die jüngere Marke 'QUALYTET' (für Bürostühle). Wie wirkt die schwache Kennzeichnungskraft der älteren Marke auf die Beurteilung der Verwechslungsgefahr?",
    options: [
      "Bei identischen Waren reicht jede Zeichenähnlichkeit für Verwechslungsgefahr aus.",
      "Schwache Kennzeichnungskraft engert den Schutzbereich ein — trotz hoher Warenidentität ist Verwechslungsgefahr nur bei sehr großer Zeichenähnlichkeit anzunehmen.",
      "Die schwache Kennzeichnungskraft schließt Verwechslungsgefahr von vornherein aus.",
      "Kennzeichnungskraft ist im Rahmen von § 9 MarkenG nicht zu prüfen.",
    ],
    answers: [1],
    explanation:
      "EuGH 'Canon' (C-39/97) und § 9 Abs. 1 Nr. 2 MarkenG: Schwache Kennzeichnungskraft der älteren Marke bedeutet, dass ihr Schutzbereich enger ist — es bedarf einer höheren Zeichenähnlichkeit, um Verwechslungsgefahr zu bejahen. Im Wechselwirkungsmodell kann die hohe Warenidentität zwar kompensierend wirken, doch bei schwacher Kennzeichnungskraft müssen die Zeichen sehr ähnlich sein. 'QUALYTET' nähert sich 'QUALITÄT' klanglich und begrifflich an; ob das ausreicht, hängt vom Gesamtabwägungsergebnis ab — für das Grundprinzip ist jedoch festzuhalten: schwache Kennzeichnungskraft = enger Schutzbereich.",
    sourceIds: ["markenG9"],
    sources: [
      { id: "markenG9", label: "§ 9 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__9.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "verwechslungsgefahr", "kennzeichnungskraft", "wechselwirkung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-023",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 14 Abs. 5/6 — Ansprüche bei Verletzung",
    title: "Fall: Ansprüche des Markeninhabers bei Verletzung",
    prompt:
      "Markeninhaber M stellt fest, dass Konkurrent K seine eingetragene Marke verletzt. Welche Ansprüche stehen M nach § 14 Abs. 5 und 6 MarkenG zur Verfügung?",
    options: [
      "Nur Unterlassung — Schadensersatz setzt eine gesonderte gesetzliche Grundlage voraus.",
      "Unterlassung (§ 14 Abs. 5) und bei Vorsatz oder Fahrlässigkeit Schadensersatz (§ 14 Abs. 6); daneben §§ 18, 19 MarkenG (Vernichtung, Auskunft).",
      "Schadensersatz nur bei Vorsatz — Fahrlässigkeit reicht nicht.",
      "Nur Schadensersatz — Unterlassungsansprüche gibt es im Markenrecht nicht.",
    ],
    answers: [1],
    explanation:
      "§ 14 Abs. 5 MarkenG: Wer eine Marke verletzt, kann vom Markeninhaber auf Unterlassung in Anspruch genommen werden. § 14 Abs. 6 MarkenG: Wer die Verletzungshandlung vorsätzlich oder fahrlässig vornimmt, ist dem Inhaber zum Ersatz des dadurch entstandenen Schadens verpflichtet. Daneben stehen dem Inhaber Ansprüche auf Vernichtung (§ 18 MarkenG), Auskunft (§ 19 MarkenG) sowie ggf. Beseitigung zu. Der Schadensersatz kann nach drei Methoden berechnet werden: konkreter Schaden, entgangener Gewinn oder Lizenzanalogie.",
    sourceIds: ["markenG14"],
    sources: [
      { id: "markenG14", label: "§ 14 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__14.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["markenrecht", "verletzung", "ansprueche"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-024",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 5 — Geschäftliche Bezeichnungen",
    title: "Unternehmenskennzeichen vs. Marke (§ 5 MarkenG)",
    prompt:
      "Was unterscheidet das Unternehmenskennzeichen (§ 5 Abs. 2 MarkenG) von der eingetragenen Marke hinsichtlich Entstehung und Schutzgegenstand?",
    options: [
      "Marke schützt Waren/DL und entsteht durch Eintragung (Regelfall); Unternehmenskennzeichen schützt das Unternehmen als solches und entsteht durch Ingebrauchnahme mit Verkehrsgeltung oder Handelsregistereintrag.",
      "Beide schützen Waren und Dienstleistungen; der einzige Unterschied ist die behördliche Zuständigkeit (DPMA vs. Handelsregister).",
      "Unternehmenskennzeichen entsteht durch Eintragung beim DPMA; Marke entsteht automatisch durch Benutzung.",
      "Unternehmenskennzeichen genießt bundesweiten Schutz; Marke nur regional.",
    ],
    answers: [0],
    explanation:
      "§ 5 Abs. 2 MarkenG: Unternehmenskennzeichen (Firmennamen, besondere Geschäftsbezeichnungen, Firmenabkürzungen) schützen das Unternehmen als solches — nicht spezifisch Waren oder Dienstleistungen. Der Schutz entsteht durch Ingebrauchnahme, sofern das Zeichen Verkehrsgeltung erworben hat, oder durch Eintragung ins Handelsregister. Im Gegensatz dazu schützt die eingetragene Marke (§ 4 Nr. 1 MarkenG) Waren und Dienstleistungen; Schutz entsteht durch Eintragung beim DPMA. Der Schutzbereich des Unternehmenskennzeichens ist territorial auf das Gebiet der tatsächlichen Benutzung begrenzt (kein automatisch bundesweiter Schutz).",
    sourceIds: ["markenG5"],
    sources: [
      { id: "markenG5", label: "§ 5 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__5.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "unternehmenskennzeichen", "geschaeftliche-bezeichnung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-025",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 6 — Priorität",
    title: "Prioritätsprinzip und Zeitrang (§ 6 MarkenG)",
    prompt:
      "Markeninhaber A hat eine Marke in Deutschland am 1. März 2022 angemeldet. Markeninhaber B hat eine inhaltsgleiche Marke für ähnliche Waren bereits seit Januar 2022 intensiv benutzt und damit Verkehrsgeltung erworben. Wer hat den besseren Zeitrang?",
    options: [
      "A — weil er die Marke als Erster beim DPMA angemeldet hat.",
      "B — weil seine Benutzungsmarke bereits vor der Anmeldung von A durch Verkehrsgeltung entstanden ist; der Zeitrang richtet sich nach § 6 Abs. 3 MarkenG (Ingebrauchnahme).",
      "Beide haben denselben Zeitrang — eingetragene Marken und Benutzungsmarken sind gleichrangig.",
      "A — weil die Eintragungsmarke im deutschen Recht immer Vorrang vor der Benutzungsmarke hat.",
    ],
    answers: [1],
    explanation:
      "§ 6 Abs. 1 MarkenG: Bei Kollision gilt der ältere Zeitrang. § 6 Abs. 2 MarkenG: Für eingetragene Marken ist der Anmeldetag maßgeblich. § 6 Abs. 3 MarkenG: Für Benutzungsmarken (§ 4 Nr. 2) und notorisch bekannte Marken (§ 4 Nr. 3) ist der Zeitpunkt maßgeblich, zu dem sie entstanden sind (Ingebrauchnahme mit Verkehrsgeltung). Da B's Marke durch Benutzung bereits im Januar 2022 entstanden ist, hat B den früheren Zeitrang gegenüber A's Anmeldung vom 1. März 2022.",
    sourceIds: ["markenG6"],
    sources: [
      { id: "markenG6", label: "§ 6 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__6.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "prioritaet", "zeitrang"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-026",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "MarkenG § 8 Abs. 2 — Absolute Schutzhindernisse",
    title: "Absolute Schutzhindernisse — Prüfung von Amts wegen",
    prompt:
      "Welche der folgenden Aussagen zu den absoluten Eintragungshindernissen (§ 8 MarkenG) sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Absolute Schutzhindernisse werden vom DPMA von Amts wegen geprüft — ohne Antrag eines Dritten.",
      "Absolute Schutzhindernisse können im Widerspruchsverfahren nach § 42 MarkenG geltend gemacht werden.",
      "Bösgläubige Anmeldung (§ 8 Abs. 2 Nr. 14 MarkenG) ist ein absolutes Schutzhindernis.",
      "Relative Schutzhindernisse (§ 9 MarkenG) werden ebenfalls von Amts wegen geprüft.",
    ],
    answers: [0, 2],
    explanation:
      "Zutreffend sind Aussagen A und C: (A) § 8 Abs. 2 MarkenG — absolute Hindernisse werden vom DPMA im Prüfungsverfahren von Amts wegen geprüft; ein Antrag Dritter ist nicht erforderlich. (C) § 8 Abs. 2 Nr. 14 MarkenG: Bösgläubige Anmeldungen gehören zu den absoluten Hindernissen. Nicht zutreffend: (B) Widerspruch nach § 42 MarkenG ist das Instrument für relative Schutzhindernisse (ältere Rechte Dritter), nicht für absolute Hindernisse (diese werden im Nichtigkeitsverfahren nach § 50 MarkenG geltend gemacht). (D) Relative Schutzhindernisse (§ 9) werden gerade nicht von Amts wegen geprüft — sie setzen einen Widerspruch oder eine Klage des Rechtsinhabers voraus.",
    sourceIds: ["markenG8", "markenG42"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
      { id: "markenG42", label: "§ 42 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__42.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "schutzhindernisse", "verfahren"],
    // REVIEW (question-reviewer 2026-06-03): FIX — Boesglaeubigkeit von "§ 8 Abs. 2 Nr. 10" auf
    // "Nr. 14" korrigiert (Option C + Explanation). Post-MaMoG 2019 ist boesglaeubige Anmeldung
    // § 8 Abs. 2 Nr. 14 MarkenG; Nr. 10 bezeichnet jetzt geschuetzte geografische Angaben/
    // Ursprungsbezeichnungen. Substanz (boesglaeubig = absolutes Hindernis) war korrekt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-027",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 9 — Markenähnlichkeit",
    title: "Fall: Zeichenvergleich — Gesamteindrucksgrundsatz",
    prompt:
      "Die ältere Marke 'ADISTAR' trifft auf die jüngere zusammengesetzte Wort-/Bildmarke 'ADISTAR SPORTS & Design'. Das Bildele­ment ist rein dekorativ. Wie ist der Zeichenvergleich vorzunehmen?",
    options: [
      "Jedes Element ist isoliert zu vergleichen; das Bildelement muss separat geprüft werden.",
      "Maßgeblich ist der Gesamteindruck — schwache oder rein dekorative Bestandteile beeinflussen ihn weniger; prägender Bestandteil kann 'ADISTAR' sein.",
      "Zusammengesetzte Marken können nie mit Wortmarken verwechselt werden.",
      "Das Bildelement ist stets prägend, weil Bilder einprägsamer sind als Wörter.",
    ],
    answers: [1],
    explanation:
      "§ 9 Abs. 1 Nr. 2 MarkenG und Gesamteindrucksgrundsatz (EuGH 'Sabel/Puma', C-251/95): Der Vergleich erfolgt nach dem Gesamteindruck, den die Marken beim Durchschnittskonsumenten hinterlassen — nicht nach zergliederndem Einzelvergleich. In zusammengesetzten Marken kann ein Bestandteil prägen, wenn er die anderen deutlich überwiegt. Rein dekorative oder schwache Bestandteile ('SPORTS' für Sportartikel, ein dekoratives Bildelement) tragen wenig zum Gesamteindruck bei. Dadurch kann der Wortbestandteil 'ADISTAR' prägend sein — mit der Folge einer klanglichen und visuellen Ähnlichkeit zur älteren Marke 'ADISTAR' (vgl. EuGH 'Medion/Thomson', C-120/04).",
    sourceIds: ["markenG9"],
    sources: [
      { id: "markenG9", label: "§ 9 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__9.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "verwechslungsgefahr", "zeichenaehnlichkeit", "gesamteindruck"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-028",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Unionsmarke — Art. 7 Abs. 2 UMV",
    title: "Einheitsprinzip der Unionsmarke",
    prompt:
      "Das Zeichen 'NORDIC FRESH' soll als Unionsmarke für Tiefkühlkost eingetragen werden. In Deutschland und Österreich ist das Zeichen für Tiefkühlkost beschreibend. In anderen EU-Mitgliedstaaten wäre es eintragungsfähig. Was ist die Konsequenz nach Art. 7 Abs. 2 UMV?",
    options: [
      "Die Unionsmarke kann für alle EU-Mitgliedstaaten eingetragen werden, in denen keine Beschreibungsnähe besteht.",
      "Die Unionsmarke ist zurückzuweisen, wenn das Hindernis auch nur in einem Teil der EU — z.B. in einem Mitgliedstaat — vorliegt.",
      "Eine Teilzurückweisung für Deutschland und Österreich ist möglich; im Rest der EU wird die Marke eingetragen.",
      "Die Beschreibungsnähe in zwei Mitgliedstaaten ist unbeachtlich, wenn die Mehrheit der EU-Mitgliedstaaten Schutz gewähren würde.",
    ],
    answers: [1],
    explanation:
      "Art. 7 Abs. 2 UMV: Die absoluten Eintragungshindernisse greifen auch dann, wenn sie nur in einem Teil der Europäischen Union vorliegen. Dies ist Ausdruck des Einheitsprinzips der Unionsmarke: Die UM hat unitären Charakter und muss in der gesamten EU schutzfähig sein. Schon eine Schutzunfähigkeit in einem Mitgliedstaat (hier: Deutschland und Österreich) führt zur Zurückweisung der gesamten Anmeldung. Eine Teilzurückweisung (territorial begrenzt) ist bei der Unionsmarke grundsätzlich nicht möglich; stattdessen kann der Anmelder die Anmeldung in nationale Anmeldungen umwandeln (Art. 139 ff. UMV).",
    sourceIds: ["umv7"],
    sources: [
      { id: "umv7", label: "Art. 7 UMV", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["markenrecht", "unionsmarke", "einheitsprinzip"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-029",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 8 Abs. 2 Nr. 4 — Täuschungsgefahr",
    title: "Täuschungsgefahr als absolutes Schutzhindernis",
    prompt:
      "Das Zeichen 'SWISS MADE' soll für in China produzierte Uhren eingetragen werden. Welches absolute Schutzhindernis greift?",
    options: [
      "§ 8 Abs. 2 Nr. 1 MarkenG — fehlende Unterscheidungskraft",
      "§ 8 Abs. 2 Nr. 2 MarkenG — Freihaltebedürfnis für geografische Angaben",
      "§ 8 Abs. 2 Nr. 4 MarkenG — Täuschungsgefahr über die geografische Herkunft der Waren",
      "§ 8 Abs. 2 Nr. 3 MarkenG — Gattungsbezeichnung",
    ],
    answers: [2],
    explanation:
      "§ 8 Abs. 2 Nr. 4 MarkenG: Von der Eintragung ausgeschlossen sind Marken, die geeignet sind, das Publikum insbesondere über die Art, die Beschaffenheit oder die geografische Herkunft der Waren oder Dienstleistungen zu täuschen. 'SWISS MADE' für in China produzierte Uhren erweckt den Eindruck einer schweizerischen Herkunft, die nicht besteht — ein klassischer Fall der Täuschungsgefahr über die geografische Herkunft. Dieses Hindernis ist im Gegensatz zu §§ 8 Abs. 2 Nr. 1–3 nicht durch Verkehrsdurchsetzung heilbar.",
    sourceIds: ["markenG8"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "schutzhindernisse", "taueschungsgefahr"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-030",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 26 Abs. 3 — Benutzung in abgewandelter Form",
    title: "Fall: Benutzung in abgewandelter Form (§ 26 Abs. 3 MarkenG)",
    prompt:
      "Markeninhaber M hat die Wortmarke 'OPTIMA' eingetragen. Er benutzt die Marke tatsächlich stets als 'OPTIMA®' (mit ®-Zeichen) und in einer bestimmten Schrifttype. Gilt dies als rechtserhaltende Benutzung nach § 26 MarkenG?",
    options: [
      "Nein — nur die exakte eingetragene Form zählt als rechtserhaltende Benutzung.",
      "Ja — § 26 Abs. 3 MarkenG erlaubt Benutzung in abgewandelter Form, sofern die Unterscheidungskraft nicht verändert wird; ®-Symbol und Schrifttype ändern die Unterscheidungskraft nicht.",
      "Nur wenn M beim DPMA eine gesonderte Erklärung zur abgewandelten Benutzung einreicht.",
      "Nein — die Schrifttype und das ®-Symbol konstituieren eine eigenständige neue Marke.",
    ],
    answers: [1],
    explanation:
      "§ 26 Abs. 3 MarkenG: Die Benutzung der Marke in einer Form, die von der Eintragung abweicht, gilt als rechtserhaltende Benutzung, wenn die Abweichungen die Unterscheidungskraft der Marke nicht verändern — unabhängig davon, ob die veränderte Form ebenfalls als Marke eingetragen ist. Das ®-Symbol und eine bestimmte Schrifttype sind typischerweise unbedeutende Abweichungen, die die Unterscheidungskraft des Wortes 'OPTIMA' nicht berühren. Keine gesonderte Erklärung beim DPMA ist erforderlich.",
    sourceIds: ["markenG26"],
    sources: [
      { id: "markenG26", label: "§ 26 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__26.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "benutzungspflicht", "rechtserhaltende-benutzung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-031",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "MarkenG §§ 49, 50, 51 — Verfall und Nichtigkeit",
    title: "Gründe für Verfall und Nichtigkeit — Zuordnung",
    prompt:
      "Welche der folgenden Sachverhalte führen zum Verfall (§ 49 MarkenG) — nicht zur Nichtigkeit? (Mehrere Antworten möglich)",
    options: [
      "Die Marke ist nach ihrer Eintragung durch Benutzung im allgemeinen Sprachgebrauch zur Gattungsbezeichnung geworden.",
      "Die Marke hätte bei der Eintragung wegen fehlender Unterscheidungskraft zurückgewiesen werden müssen.",
      "Der Markeninhaber hat die Marke fünf Jahre lang nicht ernsthaft benutzt.",
      "Die Marke war zum Zeitpunkt der Anmeldung bösgläubig angemeldet worden.",
    ],
    answers: [0, 2],
    explanation:
      "Verfall (§ 49 MarkenG, ex nunc): (A) § 49 Abs. 2 Nr. 1 — Verwandlung in eine im Verkehr üblich gewordene Gattungsbezeichnung nach der Eintragung (Generizid, z.B. 'Aspirin', 'Tempo'); (C) § 49 Abs. 1 — fünf Jahre Nichtbenutzung. Nichtigkeit (§ 50 MarkenG, ex tunc): (B) § 50 Abs. 1 i.V.m. § 8 Abs. 2 Nr. 1 — fehlende Unterscheidungskraft zum Anmeldezeitpunkt; (D) § 50 Abs. 1 i.V.m. § 8 Abs. 2 Nr. 14 — bösgläubige Anmeldung. Der entscheidende Unterschied: Verfall entsteht nachträglich; Nichtigkeit besagt, dass die Eintragung von Anfang an hätte unterbleiben sollen.",
    sourceIds: ["markenG49", "markenG50"],
    sources: [
      { id: "markenG49", label: "§ 49 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__49.html" },
      { id: "markenG50", label: "§ 50 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__50.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["markenrecht", "verfall", "nichtigkeit"],
    // REVIEW (question-reviewer 2026-06-03): FIX — Boesglaeubigkeit in Explanation von "§ 8 Abs. 2
    // Nr. 10" auf "Nr. 14" korrigiert (post-MaMoG). Zuordnung Verfall/Nichtigkeit (answers [0,2])
    // ist korrekt: A=§ 49 Abs. 2 Nr. 1, C=§ 49 Abs. 1 (Verfall); B/D=§ 50 (Nichtigkeit).
    reviewStatus: "beta-ready",
  },

  // ─── FALLKONSTELLATIONEN (weitere MC) da-marke-032 … da-marke-036 ─────────

  {
    id: "da-marke-032",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 8 Abs. 2 Nr. 14 — Bösgläubige Anmeldung",
    title: "Bösgläubige Anmeldung (§ 8 Abs. 2 Nr. 14 MarkenG)",
    prompt:
      "Welches Verhalten ist ein klassisches Beispiel für eine bösgläubige Markenanmeldung nach § 8 Abs. 2 Nr. 14 MarkenG?",
    options: [
      "Ein Unternehmen meldet eine Marke an, die es seit Jahren intensiv benutzt.",
      "Ein Spekulant meldet eine Marke gezielt an, um einen ausländischen Hersteller, der diese Marke dort benutzt, von einer Expansion nach Deutschland abzuhalten (Sperrmarke).",
      "Ein Unternehmen erwirbt durch Kauf eine ältere Marke und meldet sie neu ein.",
      "Eine Marke wird ohne Verkehrsgeltungsnachweis beim DPMA angemeldet.",
    ],
    answers: [1],
    explanation:
      "§ 8 Abs. 2 Nr. 14 MarkenG: Bösgläubige Anmeldungen sind von der Eintragung ausgeschlossen. Bösgläubigkeit liegt u.a. vor bei Anmeldung in Behinderungsabsicht (Sperrmarken), ohne eigene Benutzungsabsicht, zum Zweck des Abfangens ausländischer Marken, deren Benutzung im Inland geplant ist (Auslandsmarkenabgriff), oder zur Spekulation auf Lizenzeinnahmen. Legitime Neuanmeldungen nach Markteinführung, Unternehmenserwerb mit Markenübertragung oder Anmeldungen ohne separaten Verkehrsgeltungsnachweis sind keine Fälle der Bösgläubigkeit.",
    sourceIds: ["markenG8"],
    sources: [
      { id: "markenG8", label: "§ 8 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__8.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "schutzhindernisse", "boesglaeubiganmeldung"],
    // REVIEW (question-reviewer 2026-06-03): FIX — alle "§ 8 Abs. 2 Nr. 10"-Zitate (topic, title,
    // prompt, explanation) auf "Nr. 14" korrigiert. Post-MaMoG 2019 ist boesglaeubige Anmeldung
    // § 8 Abs. 2 Nr. 14 MarkenG. Sachverhalt/Antwortschluessel (Sperrmarke = boesglaeubig) korrekt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-033",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Unionsmarke — Verhältnis zur nationalen Marke",
    title: "Unitärer Charakter der Unionsmarke und Umwandlung",
    prompt:
      "Das EUIPO lehnt eine Unionsmarkenanmeldung ab, weil die Marke in Spanien beschreibend ist. Was kann der Anmelder tun, um Schutz in den anderen EU-Mitgliedstaaten zu erlangen?",
    options: [
      "Er kann die Unionsmarkenanmeldung auf die übrigen 26 Mitgliedstaaten beschränken.",
      "Er kann die Unionsmarkenanmeldung in nationale Anmeldungen umwandeln (Art. 139 ff. UMV), um in den Mitgliedstaaten Schutz zu erlangen, in denen das Hindernis nicht besteht.",
      "Er kann Beschwerde beim EuGH einlegen.",
      "Er muss die Anmeldung fallen lassen — eine Zurückweisung durch das EUIPO ist endgültig.",
    ],
    answers: [1],
    explanation:
      "Art. 139 ff. UMV (Umwandlung): Wird eine Unionsmarkenanmeldung zurückgewiesen oder erlischt eine Unionsmarke, kann der Inhaber innerhalb bestimmter Fristen beantragen, die Anmeldung oder Marke in nationale Anmeldungen/Schutzrechte in den gewünschten Mitgliedstaaten umzuwandeln. Die umgewandelten nationalen Anmeldungen behalten den Zeitrang der ursprünglichen Unionsmarkenanmeldung. Eine geografische Teilung der Unionsmarke (nur bestimmte Mitgliedstaaten) ist konzeptionell nicht vorgesehen, da der unitäre Charakter eine einheitliche EU-weite Wirkung erfordert.",
    sourceIds: ["umv7"],
    sources: [
      { id: "umv7", label: "Art. 7 UMV", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:02017R1001" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["markenrecht", "unionsmarke", "umwandlung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-034",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 14 — Verletzung / § 23 — Schranken",
    title: "Fall: Eigener Name als Schranke (§ 23 Abs. 1 Nr. 1 MarkenG)",
    prompt:
      "Rechtsanwalt Franz Müller betreibt eine Kanzlei unter dem Schild 'Müller & Partner'. Es besteht eine eingetragene Marke 'MÜLLER' für Rechtsdienstleistungen. Kann der Markeninhaber Herrn Müller die Verwendung seines Namens untersagen?",
    options: [
      "Ja — wer seinen Namen gewerblich verwendet, verletzt eine gleichlautende Marke stets.",
      "Nein — § 23 Abs. 1 Nr. 1 MarkenG erlaubt natürlichen Personen die Benutzung ihres eigenen Namens, sofern dies den anständigen Gepflogenheiten entspricht.",
      "Ja — § 23 MarkenG gilt nur für Firmennamen juristischer Personen, nicht für Individualnamen.",
      "Nein — weil 'Müller' kein ausreichend unterscheidungskräftiges Zeichen ist.",
    ],
    answers: [1],
    explanation:
      "§ 23 Abs. 1 Nr. 1 MarkenG: Eine Marke gibt dem Inhaber nicht das Recht, einem Dritten zu untersagen, im geschäftlichen Verkehr seinen eigenen Namen oder seine Adresse zu benutzen — sofern die Benutzung den anständigen Gepflogenheiten in Gewerbe oder Handel entspricht (Lauterkeitskorrektiv). Herr Müller darf seinen bürgerlichen Namen 'Müller' in der Kanzleibezeichnung verwenden, auch wenn eine Marke 'MÜLLER' für Rechtsdienstleistungen eingetragen ist. Das Lauterkeitskorrektiv schließt lediglich missbräuchliche Verwendungsformen aus (z.B. wenn der Nachname bewusst markenähnlich in Szene gesetzt wird).",
    sourceIds: ["markenG23"],
    sources: [
      { id: "markenG23", label: "§ 23 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__23.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "schranken", "eigenname"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-035",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 9 — Warenähnlichkeit",
    title: "Fall: Warenähnlichkeit nach Canon-Kriterien",
    prompt:
      "Die ältere Marke 'PEAK' ist für Kletterausrüstung eingetragen. Die jüngere Marke 'PEAK' wird für Skischuhe angemeldet. Ist Warenähnlichkeit zu bejahen?",
    options: [
      "Nein — Kletterausrüstung und Skischuhe sind völlig verschiedene Waren ohne jede Ähnlichkeit.",
      "Ja — beide sind Sportartikel mit überschneidenden Vertriebskanälen (Sportfachhandel), ähnlichem Verwendungszweck (Outdoor-Sport) und typischerweise gleichen Herstellern — Warenähnlichkeit ist anzunehmen.",
      "Die Warenähnlichkeit bestimmt sich ausschließlich nach der Nizza-Klasse; unterschiedliche Klassen bedeuten stets Unähnlichkeit.",
      "Warenähnlichkeit ist nur bei identischen Waren zu prüfen; bei verschiedenen Waren entfällt die Prüfung.",
    ],
    answers: [1],
    explanation:
      "EuGH 'Canon' (C-39/97) und § 9 Abs. 1 Nr. 2 MarkenG: Warenähnlichkeit beurteilt sich nicht nach der Klasseneinteilung (die hat keine unmittelbare Auswirkung), sondern nach einem Kriterienkatalog: gleiche Verwendungszwecke (Outdoor-Sport), überschneidende Vertriebskanäle (Sportfachhandel), Wettbewerbsverhältnis (beide für aktive Outdoor-Sportler), typische Herstelleridentität (Outdoor-Marken produzieren oft breite Sortimente). Kletterausrüstung und Skischuhe teilen wesentliche Kriterien — Warenähnlichkeit ist jedenfalls anzunehmen, auch wenn keine Identität vorliegt.",
    sourceIds: ["markenG9"],
    sources: [
      { id: "markenG9", label: "§ 9 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__9.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "verwechslungsgefahr", "warenaehnlichkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-036",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "MarkenG § 14 / § 25 — Einrede mangelnder Benutzung",
    title: "Fall: Einrede der mangelnden Benutzung im Verletzungsverfahren",
    prompt:
      "Markeninhaber M klagt wegen Verletzung seiner seit acht Jahren eingetragenen Marke 'SOLARIS'. Beklagter B wendet ein, M benutze die Marke nicht. Wie verhält es sich nach § 25 MarkenG?",
    options: [
      "Die Einrede ist unbeachtlich — M kann auch ohne Benutzung klagen.",
      "B muss beweisen, dass M die Marke nie benutzt hat.",
      "Wenn B die Einrede der mangelnden Benutzung erhebt (§ 25 MarkenG), muss M glaubhaft machen, dass er die Marke in den fünf Jahren vor Klageerhebung ernsthaft benutzt hat (§ 26 MarkenG) — gelingt ihm das nicht, sind seine Ansprüche ausgeschlossen.",
      "M hat acht Jahre nach Eintragung eine unwiderlegliche Benutzungsvermutung — Einreden sind nach Ablauf der Schonfrist ausgeschlossen.",
    ],
    answers: [2],
    explanation:
      "§ 25 Abs. 1 MarkenG: Auf Verletzungsansprüche kann sich der Markeninhaber nicht berufen, wenn die Marke wegen mangelnder Benutzung verfallen ist. § 25 Abs. 2 MarkenG i.V.m. § 26 MarkenG: Erhebt der Beklagte die Einrede der mangelnden Benutzung, muss der Kläger (Markeninhaber) dartun und glaubhaft machen, dass die Marke in den fünf Jahren vor Klageerhebung ernsthaft benutzt wurde. Die Beweislast liegt beim Markeninhaber, nicht beim Beklagten. Die acht Jahre seit Eintragung bedeuten nur, dass die Benutzungsschonfrist längst abgelaufen ist — sie begründen keine Benutzungsvermutung.",
    sourceIds: ["markenG25", "markenG26"],
    sources: [
      { id: "markenG25", label: "§ 25 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__25.html" },
      { id: "markenG26", label: "§ 26 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__26.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["markenrecht", "benutzungspflicht", "verletzung", "einrede"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERING da-marke-037 … da-marke-038 ────────────────────────────────

  {
    id: "da-marke-037",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "MarkenG § 9 — Verwechslungsgefahr: Prüfungsschema",
    title: "Prüfungsschema Verwechslungsgefahr (§ 9 Abs. 1 Nr. 2 MarkenG)",
    prompt:
      "Bringe die Stufen der markenrechtlichen Kollisionsprüfung (Verwechslungsgefahr, § 9 Abs. 1 Nr. 2 / § 14 Abs. 2 Nr. 2 MarkenG) in die richtige gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Schutz der älteren Marke klären: Bestand, Priorität (§ 6 MarkenG) und — auf Einrede — rechtserhaltende Benutzung (§ 26 MarkenG)",
      "Kollisionstatbestand § 9 Abs. 1 Nr. 2 / § 14 Abs. 2 Nr. 2 MarkenG: Ähnlichkeit der Zeichen UND Ähnlichkeit der Waren/Dienstleistungen feststellen",
      "Verwechslungsgefahr als Gesamtabwägung nach dem Wechselwirkungsprinzip (EuGH 'Canon'): Zeichenähnlichkeit, Waren-/DL-Ähnlichkeit und Kennzeichnungskraft der älteren Marke ins Verhältnis setzen",
      "Ergebnis: Schranken (§§ 23, 24 MarkenG) verneinen, dann Rechtsfolgen (Widerspruch/Löschung bzw. Ansprüche § 14 Abs. 5/6 MarkenG)",
    ],
    explanation:
      "Die Kollisionsprüfung folgt einer festen Grobstruktur: zuerst Schutz und Vorrang der älteren Marke (Bestand, Priorität § 6, ggf. rechtserhaltende Benutzung § 26), dann der Kollisionstatbestand (Zeichen- UND Waren-/DL-Ähnlichkeit), anschließend die Verwechslungsgefahr als Gesamtabwägung nach dem Wechselwirkungsprinzip (EuGH 'Canon': je ähnlicher die Waren/DL und je kennzeichnungskräftiger die ältere Marke, desto geringere Zeichenähnlichkeit genügt — und umgekehrt), zuletzt Schranken und Rechtsfolgen. Innerhalb der Gesamtabwägung ist die Reihenfolge, in der Kennzeichnungskraft, Zeichen- und Warenähnlichkeit erörtert werden, nicht starr; entscheidend ist ihre wertende Zusammenschau.",
    sourceIds: ["markenG9"],
    sources: [
      { id: "markenG9", label: "§ 9 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__9.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["markenrecht", "verwechslungsgefahr", "pruefungsschema"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-038",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "MarkenG — Anmeldeverfahren beim DPMA",
    title: "Ablauf der Markenanmeldung beim DPMA",
    prompt:
      "Bringe die Stationen der Markenanmeldung beim DPMA bis zur Eintragung in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Schriftliche Anmeldung beim DPMA mit Mindestinhalt: Antrag, Angaben zum Anmelder, Markenwiedergabe, Waren-/Dienstleistungsverzeichnis (§ 32 MarkenG)",
      "Zahlung der Anmeldegebühr — Zeitrang entsteht mit Eingang der Mindestanforderungen beim DPMA (§ 33 MarkenG)",
      "Formale und amtliche Prüfung auf absolute Schutzhindernisse (§ 8 MarkenG) durch DPMA-Prüfer",
      "Eintragung und Veröffentlichung der Marke im Markenblatt",
      "Widerspruchsfrist von drei Monaten ab Veröffentlichung (§ 42 MarkenG) — Dritte können wegen älterer Rechte widersprechen",
    ],
    explanation:
      "Das DPMA-Anmeldeverfahren läuft in folgenden Stationen: (1) Anmeldung mit Mindestinhalt (§ 32 MarkenG) — Zeitrang entsteht; (2) Gebührenzahlung — ohne Gebühr kein Zeitrang; (3) Amtliche Prüfung der absoluten Schutzhindernisse (§ 8 MarkenG) von Amts wegen; (4) Bei Eintragungsreife: Eintragung und Veröffentlichung; (5) Dreimonatige Widerspruchsfrist (§ 42 MarkenG) für Inhaber älterer Rechte (relative Schutzhindernisse werden nur auf Widerspruch geprüft, nicht von Amts wegen).",
    sourceIds: ["markenG32", "markenG42"],
    sources: [
      { id: "markenG32", label: "§ 32 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__32.html" },
      { id: "markenG42", label: "§ 42 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__42.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["markenrecht", "verfahren", "dpma-anmeldung"],
    reviewStatus: "beta-ready",
  },

  // ─── CLOZE da-marke-039 … da-marke-040 ──────────────────────────────────

  {
    id: "da-marke-039",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "MarkenG § 26 — Benutzungsschonfrist",
    title: "Cloze: Benutzungsschonfrist (§ 26 MarkenG)",
    prompt:
      "Eine eingetragene Marke muss nach § 26 Abs. 1 MarkenG innerhalb von ___ Jahren nach der Eintragung ernsthaft benutzt werden, andernfalls droht der Verfall nach § 49 MarkenG.",
    options: ["3", "5", "7", "10"],
    answers: [1],
    explanation:
      "§ 26 Abs. 1 MarkenG: Die Benutzungsschonfrist beträgt fünf Jahre ab der Eintragung. Wird die Marke innerhalb dieser Frist nicht ernsthaft benutzt, droht der Verfall nach § 49 Abs. 1 MarkenG. Im Verletzungsverfahren kann der Beklagte die Einrede der mangelnden Benutzung erheben (§ 25 MarkenG), wenn die Schonfrist abgelaufen ist. Die fünfjährige Frist gilt entsprechend für die Unionsmarke (Art. 18 UMV).",
    sourceIds: ["markenG26"],
    sources: [
      { id: "markenG26", label: "§ 26 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__26.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["markenrecht", "benutzungspflicht", "fristen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "da-marke-040",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "MarkenG § 42 — Widerspruchsfrist",
    title: "Cloze: Widerspruchsfrist (§ 42 MarkenG)",
    prompt:
      "Gegen eine beim DPMA eingetragene Marke kann innerhalb von ___ Monaten ab der Veröffentlichung der Eintragung Widerspruch eingelegt werden (§ 42 Abs. 1 MarkenG).",
    options: ["1", "3", "6", "12"],
    answers: [1],
    explanation:
      "§ 42 Abs. 1 MarkenG: Die Widerspruchsfrist beträgt drei Monate ab dem Tag der Veröffentlichung der Eintragung. Der Widerspruch kann von Inhabern älterer Marken oder geschäftlicher Bezeichnungen eingelegt werden (relative Schutzhindernisse). Im Widerspruchsverfahren kann die Benutzung der älteren Marke gefordert werden, wenn deren Benutzungsschonfrist abgelaufen ist (§ 43 MarkenG — Glaubhaftmachung der Benutzung). Für die Unionsmarke gilt eine vergleichbare Widerspruchsfrist nach Art. 46 UMV (drei Monate ab Veröffentlichung).",
    sourceIds: ["markenG42"],
    sources: [
      { id: "markenG42", label: "§ 42 MarkenG", url: "https://www.gesetze-im-internet.de/markeng/__42.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["markenrecht", "verfahren", "fristen", "widerspruch"],
    reviewStatus: "beta-ready",
  },
];
